import { useState, useEffect, SyntheticEvent } from "react";
import { Box, Grid, Card, CardContent, Typography, Button, FormControl, TextField, InputLabel, MenuItem, Select, useMediaQuery, Checkbox, FormGroup, FormControlLabel, SelectChangeEvent, Paper, CircularProgress, Divider, } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import Title from "../../components/Title";
import ModalClientHistory from "./modals/ModalClientHistory";
import ClientChangeHistoryModal from "../../components/common/ClientChangeHistoryModal";
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import AddAlertIcon from '@mui/icons-material/AddAlert';
import Tooltip from '@mui/material/Tooltip';
import { useDrawer } from "../../components/context/DrawerContext";
import { stickyListRegionWidth } from "../../components/layouts/StickyListToolbar";
import SimpleCustomTable from "../../components/table/SimpleCustomTable";
import { SimpleColumn, SimpleRow, SimpleColumnGroup, RowId } from "../../components/table/interface";
import HeaderResponsive from "../../components/layouts/HeaderResponsive";
import EmptyStateMessage from "../../components/layouts/EmptyStateMessage";
import moment from "moment-timezone";
import { minYmd, nowOperationalDateString } from '../../utils/operationalDateTime';
import { useResourcePermissions } from "../../hooks/useResourcePermissions";
import PermissionButton from "../../components/common/PermissionButton";
import { iconActionColumnSizing, simpleTableIconButtonSx } from "../../components/table/iconActionColumnSizing";

// Interfaces mejoradas
interface Office {
  id: number;
  name: string;
}

interface Collect {
  id: number;
  name: string;
}

interface Client {
  id: number;
  idnumber: string;
  fullname: string;
  phone: string;
  backup_alert_active?: boolean;
  backups?: Array<{ id: number }>;
}

interface Lend {
  id: number;
  date: string;
  date_state: string;
  contract?: string;
  value: number;
  total_value: number;
  days_term: number;
  utility: number;
  value_utility: number;
  insurance_charge: number;
  current_pay: number;
  client: Client;
  collect?: {
    id: number;
    name: string;
  };
}

interface FilterState {
  startDate: string;
  endDate: string;
  officeId: string;
  selectedCollects: number[];
}

interface ReportePrestamoFechaProps {
  showAlert: (
    message: string,
    severity?: "error" | "info" | "success" | "warning"
  ) => void;
  showLoading: () => void;
  hideLoading: () => void;
}

// Funciones utilitarias
const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    minimumFractionDigits: 0,
  }).format(value);
};

const formatDate = (dateString?: string | null): string => {
  if (!dateString) return "00/00/0000";
  const isDateTime = /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/.test(dateString);
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
  const parsed = isDateTime
    ? moment.tz(dateString, "YYYY-MM-DD HH:mm:ss", "America/Bogota")
    : isDateOnly
    ? moment.tz(dateString, "YYYY-MM-DD", "America/Bogota")
    : moment.tz(dateString, "America/Bogota");

  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "00/00/0000";
};




// Componente principal con lógica mejorada
const ReportePrestamoFecha: React.FC<ReportePrestamoFechaProps> = ({
  showAlert,
  showLoading,
  hideLoading,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentDate = nowOperationalDateString();
  const [datas, setDatas] = useState<any[]>([]);

  // Estados
  const [filter, setFilter] = useState<FilterState>({
    startDate: currentDate,
    endDate: currentDate,
    officeId: "",
    selectedCollects: [],
  });
  const [offices, setOffices] = useState<Office[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [rows, setRows] = useState<SimpleRow[]>([]);
  const [summary, setSummary] = useState({
    totalPayments: 0,
    totalLoans: 0,
    count: 0,
  });
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [openChangeModal, setOpenChangeModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [selectedClientForChanges, setSelectedClientForChanges] = useState<any>(null);
  const { drawerOpen } = useDrawer();
  const { canRead } = useResourcePermissions('/informes/reporteprestamo');
  const { canManageBackupAlert } = useResourcePermissions('/informes/historicocliente');

  // Efecto para cargar oficinas al montar el componente
  useEffect(() => {
    const fetchOffices = async () => {
      try {
        const response = await axios.get("/offices");
        if (response.data && response.data.data) {
          setOffices(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching offices:", error);
        showAlert(
          "Error al cargar las oficinas. Intente de nuevo más tarde.",
          "error"
        );
      }
    };

    fetchOffices();
  }, [showAlert]);

  // Efecto para cargar cobros cuando cambia la oficina
  useEffect(() => {
    const fetchCollects = async () => {
      if (!filter.officeId) return;

      setLoading(true);
      try {
        const response = await axios.get(`/collectsoffice/${filter.officeId}`);
        if (response.data) {
          setCollectsOffice(response.data);
          // Reset collect selection when office changes
          setFilter((prev) => ({ ...prev, selectedCollects: [] }));
          setSelectAll(false);
        }
      } catch (error) {
        console.error("Error fetching collects:", error);
        showAlert(
          "Error al cargar los cobros para la oficina seleccionada.",
          "error"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCollects();
  }, [filter.officeId, showAlert]);

  // Manejadores de eventos
  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };

  const handleOfficeChange = (e: SelectChangeEvent<string>) => {
    setFilter((prev) => ({ ...prev, officeId: e.target.value }));
  };

  const handleSelectAllCollects = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    setFilter((prev) => ({
      ...prev,
      selectedCollects: checked ? collectsOffice.map((c) => c.id) : [],
    }));
  };

  const handleCollectChange = (collectId: number, checked: boolean) => {
    setFilter((prev) => {
      const newSelected = checked
        ? [...prev.selectedCollects, collectId]
        : prev.selectedCollects.filter((id) => id !== collectId);

      // Update selectAll state based on whether all collects are selected
      setSelectAll(newSelected.length === collectsOffice.length);

      return { ...prev, selectedCollects: newSelected };
    });
  };

  const validateForm = (): boolean => {
    if (filter.selectedCollects.length === 0) {
      showAlert("Debe seleccionar al menos un cobro", "warning");
      return false;
    }

    if (new Date(filter.startDate) > new Date(filter.endDate)) {
      showAlert(
        "La fecha de inicio no puede ser mayor a la fecha final",
        "warning"
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    showLoading();
    setLoading(true);

    try {
      const response = await axios.post("/inform/createdlend", {
        start_date: filter.startDate,
        end_date: filter.endDate,
        collect_Id: filter.selectedCollects,
      });
      setDatas(response.data.data);
      //console.log("saludos,", response.data.data);
      const lends: Lend[] = response.data.data;
      //console.log("Lends fetched:", lends);
      if (!lends || lends.length === 0) {
        showAlert(
          "No se encontraron préstamos en el período seleccionado",
          "info"
        );
        setRows([]);
        setSummary({
          totalPayments: 0,
          totalLoans: 0,
          count: 0,
        });
        return;
      }

      const sortedLends = [...lends].sort((a, b) => {
        const collectCompare = String(a.collect?.name || "").localeCompare(
          String(b.collect?.name || ""),
          "es",
          { sensitivity: "base" }
        );
        if (collectCompare !== 0) return collectCompare;

        return moment(b.date).valueOf() - moment(a.date).valueOf();
      });

      const mappedRows: SimpleRow[] = sortedLends
        .map((lend) => {
          const loanMoment = moment.tz(lend.date, "America/Bogota");
          return {
            id: lend.id as RowId,
            collect: lend.collect?.name || "",
            collectId: lend.collect?.id || 0,
            idnumber: lend.client.idnumber,
            clientName: lend.client.fullname,
            phone: lend.client.phone,
            backups: lend.client?.backups || [],
            backupAlertActive:
              Boolean(lend.client?.backup_alert_active) &&
              Array.isArray(lend.client?.backups) &&
              lend.client.backups.length > 0,
            days_term: lend.days_term,
            utility: Number(lend.utility ?? 0),
            value: lend.value,
            total_value: lend.total_value,
            value_utility: Number(lend.value_utility ?? 0),
            insurance_charge: Number(lend.insurance_charge ?? 0),
            current_pay: lend.current_pay,
            contract: lend.contract || "",
            date_state: formatDate(lend.date_state),
            date: formatDate(lend.date),
            loanDateYmd: loanMoment.isValid() ? loanMoment.format("YYYY-MM-DD") : "",
            loanDateTs: loanMoment.isValid() ? loanMoment.valueOf() : 0,
            collect_name: lend.collect?.name || "",
            fullClient: lend.client,
          };
        });

      // Calculate totals
      const totalValue = mappedRows.reduce((sum, row) => sum + Number(row.value ?? 0), 0);
      const totalTotalValue = mappedRows.reduce((sum, row) => sum + Number(row.total_value ?? 0), 0);
      const totalUtility = mappedRows.reduce((sum, row) => sum + Number(row.value_utility ?? 0), 0);
      const totalInsuranceCharge = mappedRows.reduce((sum, row) => sum + Number(row.insurance_charge ?? 0), 0);

      // Create total row
      const totalRow: SimpleRow = {
        id: 'TOTAL' as RowId,
        clientName: '',
        value: totalValue,
        total_value: totalTotalValue,
        utility: '',
        value_utility: totalUtility,
        insurance_charge: totalInsuranceCharge,
        // Set other fields to empty
        idnumber: '',
        phone: '',
        contract: '',
        days_term: '',
        collect: '',
        date: '',
        date_state: '',
        collectId: 0,
        backups: [],
        backupAlertActive: false,
        current_pay: 0,
        loanDateYmd: '',
        loanDateTs: 0,
        collect_name: '',
        fullClient: null,
      };

      setRows([...mappedRows, totalRow]);
      showAlert(`Se encontraron ${lends.length} préstamos`, "success")
    } catch (error) {
      console.error("Error fetching cancellations:", error);
      showAlert(
        "Error al obtener las préstamos. Inténtelo de nuevo más tarde.",
        "error"
      );
    } finally {
      hideLoading();
      setLoading(false);
    }
  };

  const handleDeleteLend = async (lendId: number) => {
    if (!lendId) return;

    if (!window.confirm('¿Está seguro de eliminar el préstamo? Esta acción no se puede deshacer.')) {
      return;
    }

    showLoading();
    try {
      await axios.post('/lends/delete', { ids: [lendId] });
      showAlert('Préstamo eliminado correctamente', 'success');
      setRows((prev) => prev.filter((row) => row.id !== lendId));
    } catch (error) {
      console.error('Error eliminando préstamo:', error);
      showAlert('Ocurrió un error al eliminar', 'error');
    } finally {
      hideLoading();
    }
  };

  const baseColumns: SimpleColumn[] = [
    { field: 'idnumber', headerName: 'Cédula', flex: 0.8,  minWidth: 80,  maxWidth: 100, },
    {
      field: 'clientName',
      headerName: 'Cliente',
      flex: 1.5,
      minWidth: 100,
      maxWidth: 200,
      wrapText: true,
    },
    { field: 'phone', headerName: 'Teléfono', flex: 1.5,  minWidth: 80,  maxWidth: 100, },
    { field: 'contract', headerName: 'No Contrato', flex: 1, minWidth: 100, maxWidth: 120 },
    { field: 'days_term', headerName: 'Plazo', flex: 1,  minWidth: 60,  maxWidth: 60, },
    {
      field: 'utility',
      headerName: 'Utilidad %',
      flex: 1,
      minWidth: 60,
      maxWidth: 60,
      align: 'right',
      renderCell: ({ row }: { row: SimpleRow }) => (Number(row.utility ?? 0)),
    },
    {
      field: 'insurance_charge',
      headerName: 'Valor Gestion',
      flex: 1,
      minWidth: 100,
      maxWidth: 120,
      align: 'right',
      renderCell: ({ row }: { row: SimpleRow }) => formatCurrency(Number(row.insurance_charge ?? 0)),
    },
    {
      field: 'total_value',
      headerName: 'Valor Total',
      flex: 1,
      minWidth: 80,
      maxWidth: 100,
      align: 'right',
      renderCell: ({ row }: { row: SimpleRow }) => formatCurrency(Number(row.total_value ?? 0)),
    },    
    { field: 'collect', headerName: 'Cobro', flex: 1, minWidth: 150, maxWidth:150 },
    { field: 'date', headerName: 'Fecha Préstamo', flex: 1, minWidth: 80, maxWidth: 80 ,  align: 'right'},
    { field: 'date_state', headerName: 'Fecha Cancelado', flex: 1, minWidth: 80, maxWidth: 80, align: 'right' },
  ];

  const historyColumn: SimpleColumn = {
    field: 'opt_history',
    headerName: '',
    ...iconActionColumnSizing(isMobile),
    renderCell: ({ row }: { row: SimpleRow }) => (
      <PermissionButton
        allowed={canRead}
        deniedTooltip="Sin permiso para ver historial"
        tooltip="Historial"
        variant="contained"
        color="primary"
        onClick={() => handleOpenModal(row)}
        sx={simpleTableIconButtonSx}
      >
        <HistoryIcon fontSize="small" />
      </PermissionButton>
    ),
  };

  const changeHistoryColumn: SimpleColumn = {
    field: 'opt_change_history',
    headerName: '',
    ...iconActionColumnSizing(isMobile),
    renderCell: ({ row }: { row: SimpleRow }) =>
      row.backupAlertActive ? (
        <PermissionButton
          allowed={canRead}
          deniedTooltip="Sin permiso para ver cambios del cliente"
          tooltip="Ver cambios del cliente"
          variant="contained"
          color="primary"
          onClick={() => {
            const client = row.fullClient || {};
            setSelectedClientForChanges({
              id: client.id || 0,
              backup_alert_active: Boolean(row.backupAlertActive),
              backups: row.backups || [],
            });
            setOpenChangeModal(true);
          }}
          sx={simpleTableIconButtonSx}
        >
          <AddAlertIcon sx={{ color: '#ff9800' }} fontSize="small" />
        </PermissionButton>
      ) : null,
  };

  const deleteColumn: SimpleColumn = {
    field: 'opt_delete',
    headerName: '',
    ...iconActionColumnSizing(isMobile),
    renderCell: ({ row }: { row: SimpleRow }) =>
      row.loanDateYmd === currentDate ? (
        <Button
          variant="contained"
          color="error"
          onClick={() => handleDeleteLend(Number(row.id))}
          sx={simpleTableIconButtonSx}
        >
          <Tooltip title="Eliminar">
            <DeleteIcon fontSize="small" />
          </Tooltip>
        </Button>
      ) : null,
  };

  const simpleColumns: SimpleColumn[] = [...baseColumns, historyColumn, changeHistoryColumn, deleteColumn];
  const pickColumn = (field: string) => simpleColumns.find((col) => col.field === field);
  const mobileColumns: SimpleColumn[] = [
    pickColumn('id'),
    pickColumn('clientName'),
    pickColumn('total_value'),
    pickColumn('utility'),
    pickColumn('date'),
    historyColumn,
    changeHistoryColumn,
    deleteColumn,
  ].filter((col): col is SimpleColumn => Boolean(col));

  const columnGroups: SimpleColumnGroup[] = [{ header: 'Opciones', fields: ['opt_history', 'opt_change_history', 'opt_delete'] }];
  const displayColumns = isMobile ? mobileColumns : simpleColumns;
  const displayColumnGroups = isMobile
    ? columnGroups.map(group => ({
        header: group.header,
        fields: group.fields.filter(field => mobileColumns.some(col => col.field === field)),
      })).filter(group => group.fields.length > 0)
    : columnGroups;

  const handleOpenModal = (row: SimpleRow) => {
    const client = row.fullClient || {};
    setSelectedClient({
      collectId: row.collectId || 0,
      cedula: client.idnumber?.toString() || row.idnumber?.toString() || "",
      nombre: client.fullname || row.clientName || "",
      direccion: client.mainaddress || "",
      telefono: client.phone || row.phone || "",
      oficio: client.job || "",
      calificacion: "",
      celular: client.cellphone || "",
      id: client.id || row.fullClient?.id || 0,
      backup_alert_active: Boolean(row.backupAlertActive),
      backups: row.backups || [],
    });
    setOpenModal(true);
  };

  return (
    <HeaderResponsive sx={{ py: 0, px: 0.5 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Title>Informe Préstamo Por Fecha</Title>
        <Box>
          <Button type="submit" form="form-prestamos-fecha" variant="contained" startIcon={!isMobile && <SearchIcon />}>
            {isMobile ? <SearchIcon /> : 'Buscar'}
          </Button>
        </Box>
      </Box>
      <Card
        sx={{
          width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <form id="form-prestamos-fecha" onSubmit={handleSubmit}>
          <CardContent
            sx={{
              width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto',
              flexShrink: 0,
              scrollbarColor: '#0768c9 #d9e1ea',
              '&::-webkit-scrollbar': {
                width: 12,
                height: 12,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: '#d9e1ea',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#0768c9',
                borderRadius: 999,
                border: '2px solid #d9e1ea',
              },
              '&::-webkit-scrollbar-corner': {
                backgroundColor: '#d9e1ea',
              },
            }}
          >
            <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      name="startDate"
                      size="small"
                      type="date"
                      label="Fecha Inicio"
                      value={filter.startDate}
                      onChange={handleDateChange}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ max: minYmd(filter.endDate, nowOperationalDateString()) }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      name="endDate"
                      size="small"
                      type="date"
                      label="Fecha Fin"
                      value={filter.endDate}
                      onChange={handleDateChange}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: filter.startDate, max: nowOperationalDateString() }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Oficina</InputLabel>
                      <Select
                        value={filter.officeId}
                        size="small"
                        label="Oficina"
                        onChange={handleOfficeChange}
                      >
                        {offices.map((office) => (
                          <MenuItem key={office.id} value={office.id}>
                            {office.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {collectsOffice.length > 0 && (
                    <Grid item xs={12}>
                      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                          Seleccionar Cobros:
                        </Typography>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={selectAll}
                              onChange={handleSelectAllCollects}
                              indeterminate={
                                filter.selectedCollects.length > 0 &&
                                filter.selectedCollects.length <
                                collectsOffice.length
                              }
                            />
                          }
                          label="Marcar/Desmarcar todos"
                          sx={{
                            minWidth: "200px",
                            width: isMobile ? "100%" : "auto",
                            m: 0.05,
                          }}
                        />
                      </Box>
                      <Divider sx={{ my: 0.005 }} />
                      <FormGroup row>
                        {collectsOffice.map((collect) => (
                          <FormControlLabel
                            key={collect.id}
                            control={
                              <Checkbox
                                size="small"
                                checked={filter.selectedCollects.includes(
                                  collect.id
                                )}
                                onChange={(e) =>
                                  handleCollectChange(collect.id, e.target.checked)
                                }
                                value={collect.id.toString()}
                              />
                            }
                            label={collect.name}
                            sx={{
                              minWidth: "200px",
                              width: isMobile ? "100%" : "auto",
                              m: 0.005,
                            }}
                          />
                        ))}
                      </FormGroup>
                    </Grid>
                  )}
                </Grid>
            <Divider sx={{ my: 1 }} />
          </CardContent>
        </form>
        <CardContent
          sx={{
            width: (theme) => stickyListRegionWidth(theme, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
            pt: 0,
            pb: 4,
          }}
        >
          {rows.length > 0 ? (
            <SimpleCustomTable
              rows={rows}
              columns={displayColumns}
              disableInternalScroll
              showBorders
              showCount
              exportCsv
              exportFileName="prestamos-fecha.csv"
              wrapCellContent
              columnGroups={displayColumnGroups}
              getRowCellSx={({ row }) =>
                row.id === ('total' as RowId)
                  ? {
                      backgroundColor: '#ffd3b8',
                      fontWeight: 700,
                    }
                  : undefined
              }
            />
          ) : (
            <EmptyStateMessage message="No existen registros" align="left" />
          )}
        </CardContent>
      </Card>
      <Box sx={{ height: { xs: 48, md: 32 } }} />
      <ModalClientHistory
        open={openModal}
        onClose={() => setOpenModal(false)}
        canManageBackupAlert={canManageBackupAlert}
        onBackupAlertChange={(clientId, active) => {
          setRows((current) =>
            current.map((row) =>
              Number(row.fullClient?.id || 0) === clientId
                ? {
                    ...row,
                    backupAlertActive: active && Array.isArray(row.backups) && row.backups.length > 0,
                    fullClient: {
                      ...(row.fullClient || {}),
                      backup_alert_active: active,
                    },
                  }
                : row
            )
          );
          setSelectedClient((current: any) =>
            current && Number(current.id || 0) === clientId
              ? { ...current, backup_alert_active: active }
              : current
          );
        }}
        clientData={selectedClient}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
      />
      <ClientChangeHistoryModal
        open={openChangeModal}
        onClose={() => setOpenChangeModal(false)}
        showAlert={showAlert}
        showLoading={showLoading}
        hideLoading={hideLoading}
        canManageBackupAlert={canManageBackupAlert}
        onBackupAlertChange={(clientId, active) => {
          setRows((current) =>
            current.map((row) =>
              Number(row.fullClient?.id || 0) === clientId
                ? {
                    ...row,
                    backupAlertActive: active && Array.isArray(row.backups) && row.backups.length > 0,
                    fullClient: {
                      ...(row.fullClient || {}),
                      backup_alert_active: active,
                    },
                  }
                : row
            )
          );
          setSelectedClientForChanges((current: any) =>
            current && Number(current.id || 0) === clientId
              ? { ...current, backup_alert_active: active }
              : current
          );
        }}
        clientData={selectedClientForChanges}
      />
    </HeaderResponsive >
  );
};

export default ReportePrestamoFecha;
